// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  partner/src/pages/Landing.jsx
//  Copy to: C:\quickry\partner\src\pages\Landing.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap, UtensilsCrossed, ShoppingBasket, Package, Pill,
  TrendingUp, Truck, Star, ChevronRight, ArrowRight,
  Clock, BadgePercent, Shield, Smartphone, Menu, X,
} from "lucide-react";

const C = {
  orange:"#f97316", orangeDark:"#ea580c", orangeLight:"#fff7ed",
  black:"#0a0a0a", dark:"#111827", white:"#ffffff",
  gray:"rgba(255,255,255,0.6)", grayMid:"rgba(255,255,255,0.4)",
  border:"rgba(255,255,255,0.08)",
};

const PARTNER_TYPES = [
  { icon:UtensilsCrossed, label:"Restaurant",    sub:"Food delivery",      color:"#f97316", bg:"rgba(249,115,22,0.12)",  earn:"₹50,000+" },
  { icon:ShoppingBasket,  label:"Grocery Store", sub:"10-min delivery",    color:"#16a34a", bg:"rgba(22,163,74,0.12)",   earn:"₹80,000+" },
  { icon:Package,         label:"Courier",       sub:"Same-day delivery",  color:"#2563eb", bg:"rgba(37,99,235,0.12)",   earn:"₹30,000+" },
  { icon:Pill,            label:"Medicine",      sub:"Pharmacy delivery",  color:"#7c3aed", bg:"rgba(124,58,237,0.12)",  earn:"Coming Soon" },
];

const BENEFITS = [
  { icon:TrendingUp, title:"Real-time Dashboard",  desc:"Live orders, revenue, ratings sab ek jagah",         color:"#f97316" },
  { icon:BadgePercent,title:"Weekly Payments",     desc:"Har week direct bank account mein transfer",          color:"#16a34a" },
  { icon:Truck,       title:"Our Delivery Fleet",  desc:"Apni delivery ki tension nahi — Quickry riders hain", color:"#2563eb" },
  { icon:Star,        title:"Free Marketing",      desc:"Featured listing, offers, push notifications — free", color:"#f59e0b" },
  { icon:Smartphone,  title:"Partner App",         desc:"Orders manage karo dedicated partner app pe",          color:"#7c3aed" },
  { icon:Shield,      title:"Zero Setup Cost",     desc:"Koi joining fee nahi — sirf orders pe commission",     color:"#16a34a" },
];

const STEPS = [
  { num:"01", title:"Apply Online",   desc:"5 minute mein form fill karo — business details & documents" },
  { num:"02", title:"Verification",   desc:"Humari team 24-48 ghante mein documents verify karegi"       },
  { num:"03", title:"Onboarding",     desc:"Menu/products add karo — Quickry team puri help karegi"      },
  { num:"04", title:"Go Live! 🚀",    desc:"Aapka business Quickry app pe live — orders shuru!"          },
];

const FAQS = [
  { q:"Joining fees hai kya?",                    a:"Bilkul nahi! Quickry join karna free hai. Sirf delivered orders pe 15-20% commission lete hain. Koi hidden charges nahi." },
  { q:"Documents verify hone mein kitna time?",   a:"Normal cases mein 24-48 ghante. Complete aur valid documents hone chahiye — GST, FSSAI (food ke liye), bank details." },
  { q:"Payment kab milega?",                      a:"Har week Monday ko previous week ki earnings direct bank account mein. Minimum payout ₹500. UPI bhi available hai." },
  { q:"Kya delivery khud karni padegi?",          a:"Nahi! Quickry ke apne riders hain. Aapko sirf order prepare karna hai — delivery Quickry handle karega." },
  { q:"FSSAI license nahi hai?",                  a:"Food businesses ke liye FSSAI mandatory hai. Quickry team aapko registration mein help kar sakti hai — 7-10 days mein ho jaata hai." },
];

export default function Landing() {
  const navigate   = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq,  setOpenFaq]  = useState(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior:"smooth" });
    setMenuOpen(false);
  };

  return (
    <div style={{ background:C.black, color:C.white, fontFamily:"'Plus Jakarta Sans','DM Sans',sans-serif", overflowX:"hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        html{scroll-behavior:smooth}
        ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:#333;border-radius:10px}
        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes gradText{0%,100%{background-position:0%}50%{background-position:100%}}
        .fu{animation:fadeUp .7s ease both}
        .float{animation:float 5s ease-in-out infinite}
        .pulse{animation:pulse 2s infinite}
        button,a{cursor:pointer}
        input:focus,textarea:focus,select:focus{outline:none}
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:100, padding:"0 5%", transition:"all .3s", background:scrolled?"rgba(10,10,10,0.92)":"transparent", backdropFilter:scrolled?"blur(20px)":"none", borderBottom:scrolled?`1px solid ${C.border}`:"none" }}>
        <div style={{ maxWidth:1200, margin:"0 auto", height:68, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          {/* Logo */}
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.5rem", letterSpacing:"-1px" }}>
            Quick<span style={{ color:C.orange }}>ry</span>
            <span style={{ fontSize:".65rem", color:"rgba(255,255,255,0.35)", fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:400, marginLeft:6, letterSpacing:0 }}>Partner</span>
          </div>

          {/* Desktop links */}
          <div style={{ display:"flex", alignItems:"center", gap:32 }} className="desktop-nav">
            {["how","benefits","faq"].map(id => (
              <button key={id} onClick={()=>scrollTo(id)}
                style={{ background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:".88rem", fontWeight:500, fontFamily:"inherit", textTransform:"capitalize", transition:"color .2s" }}
                onMouseEnter={e=>e.target.style.color="#fff"}
                onMouseLeave={e=>e.target.style.color="rgba(255,255,255,0.6)"}>
                {id === "how" ? "How it works" : id.charAt(0).toUpperCase()+id.slice(1)}
              </button>
            ))}
          </div>

          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <button onClick={()=>navigate("/register")}
              style={{ background:"none", border:"1.5px solid rgba(255,255,255,0.15)", borderRadius:100, padding:"9px 18px", color:"rgba(255,255,255,0.7)", fontSize:".85rem", fontWeight:600, fontFamily:"inherit", transition:"all .2s" }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.4)";e.currentTarget.style.color="#fff"}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.15)";e.currentTarget.style.color="rgba(255,255,255,0.7)"}}>
              Login
            </button>
            <button onClick={()=>navigate("/register")}
              style={{ background:`linear-gradient(135deg,${C.orangeDark},${C.orange})`, border:"none", borderRadius:100, padding:"10px 22px", color:"#fff", fontSize:".85rem", fontWeight:700, fontFamily:"inherit", boxShadow:"0 4px 16px rgba(249,115,22,0.35)", transition:"all .2s" }}
              onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(249,115,22,0.45)"}}
              onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 4px 16px rgba(249,115,22,0.35)"}}>
              Apply Now
            </button>
            <button onClick={()=>setMenuOpen(o=>!o)}
              style={{ display:"none", background:"none", border:"none", color:"#fff", padding:4 }} id="hamburger">
              {menuOpen ? <X size={22}/> : <Menu size={22}/>}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={{ background:"rgba(10,10,10,0.97)", backdropFilter:"blur(20px)", borderTop:`1px solid ${C.border}`, padding:"16px 5%", display:"flex", flexDirection:"column", gap:4 }}>
            {["How it works","Benefits","FAQ"].map((label,i) => (
              <button key={i} onClick={()=>scrollTo(["how","benefits","faq"][i])}
                style={{ background:"none", border:"none", color:"rgba(255,255,255,0.7)", fontSize:".92rem", fontWeight:500, fontFamily:"inherit", padding:"12px 0", textAlign:"left", borderBottom:`1px solid ${C.border}` }}>
                {label}
              </button>
            ))}
            <button onClick={()=>navigate("/register")}
              style={{ background:`linear-gradient(135deg,${C.orangeDark},${C.orange})`, border:"none", borderRadius:12, padding:"13px", color:"#fff", fontWeight:700, fontSize:".9rem", fontFamily:"inherit", marginTop:8 }}>
              Apply Now →
            </button>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section style={{ minHeight:"100vh", display:"flex", alignItems:"center", position:"relative", overflow:"hidden", padding:"100px 5% 60px" }}>
        {/* Background */}
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse 80% 60% at 50% 0%,rgba(249,115,22,0.18) 0%,transparent 70%),linear-gradient(to bottom,#0a0a0a,#111)", zIndex:0 }}/>
        <div style={{ position:"absolute", inset:0, backgroundImage:"linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)", backgroundSize:"60px 60px", maskImage:"radial-gradient(ellipse 70% 70% at 50% 50%,black 30%,transparent 100%)", zIndex:0 }}/>

        <div style={{ maxWidth:1200, margin:"0 auto", width:"100%", display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"center", position:"relative", zIndex:1 }}>
          {/* Left */}
          <div>
            <div className="fu" style={{ animationDelay:".1s", display:"inline-flex", alignItems:"center", gap:8, background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.25)", borderRadius:100, padding:"7px 16px", fontSize:".72rem", fontWeight:700, color:C.orange, letterSpacing:".5px", textTransform:"uppercase", marginBottom:24 }}>
              <span className="pulse" style={{ width:7, height:7, borderRadius:"50%", background:C.orange, display:"inline-block" }}/>
              Now onboarding in Akola
            </div>

            <h1 className="fu" style={{ animationDelay:".2s", fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"clamp(2.4rem,5vw,3.8rem)", lineHeight:1.05, letterSpacing:"-2px", marginBottom:22 }}>
              Grow your<br/>business with<br/>
              <span style={{ background:`linear-gradient(135deg,${C.orange},#fb923c,#fbbf24)`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
                Quickry
              </span>
            </h1>

            <p className="fu" style={{ animationDelay:".3s", fontSize:"1.05rem", color:"rgba(255,255,255,0.6)", lineHeight:1.7, marginBottom:36, maxWidth:480 }}>
              Join Akola's fastest growing delivery platform. Reach thousands of customers, get real-time orders, and grow your revenue — starting today.
            </p>

            <div className="fu" style={{ animationDelay:".4s", display:"flex", gap:14, flexWrap:"wrap" }}>
              <button onClick={()=>navigate("/register")}
                style={{ background:`linear-gradient(135deg,${C.orangeDark},${C.orange})`, border:"none", borderRadius:14, padding:"15px 30px", color:"#fff", fontWeight:700, fontSize:"1rem", fontFamily:"inherit", boxShadow:"0 8px 32px rgba(249,115,22,0.35)", transition:"all .25s", display:"flex", alignItems:"center", gap:8 }}
                onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
                onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
                Start Partnering <ArrowRight size={18}/>
              </button>
              <button onClick={()=>scrollTo("how")}
                style={{ background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.12)", borderRadius:14, padding:"15px 28px", color:"#fff", fontWeight:600, fontSize:"1rem", fontFamily:"inherit", transition:"all .25s" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}>
                See how it works
              </button>
            </div>

            {/* Stats */}
            <div className="fu" style={{ animationDelay:".5s", display:"flex", gap:32, marginTop:40, paddingTop:36, borderTop:"1px solid rgba(255,255,255,0.08)" }}>
              {[
                { num:"10K+", lbl:"Active customers" },
                { num:"500+", lbl:"Daily orders" },
                { num:"28min", lbl:"Avg delivery" },
              ].map((s,i) => (
                <div key={i}>
                  <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.8rem", color:C.white, letterSpacing:"-1px" }}>{s.num}</div>
                  <div style={{ fontSize:".72rem", color:"rgba(255,255,255,0.4)", marginTop:3, fontWeight:500 }}>{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — floating card stack */}
          <div className="fu" style={{ animationDelay:".3s", display:"flex", justifyContent:"center", alignItems:"center", position:"relative" }}>
            <div style={{ position:"absolute", width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle,rgba(249,115,22,0.2),transparent)", top:-40, left:"50%", transform:"translateX(-50%)", filter:"blur(40px)", pointerEvents:"none" }}/>
            <div className="float" style={{ width:280, background:"linear-gradient(145deg,rgba(255,255,255,0.07),rgba(255,255,255,0.03))", border:"1px solid rgba(255,255,255,0.1)", borderRadius:24, padding:24, backdropFilter:"blur(10px)", boxShadow:"0 32px 80px rgba(0,0,0,0.4)", position:"relative", zIndex:2 }}>
              {/* Dashboard preview */}
              <div style={{ background:`linear-gradient(135deg,${C.orangeDark},${C.orange})`, borderRadius:16, padding:"16px", marginBottom:20 }}>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1rem", marginBottom:4 }}>Partner Dashboard</div>
                <div style={{ fontSize:".72rem", color:"rgba(255,255,255,0.75)" }}>Akola · Live</div>
              </div>
              {/* Stats row */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:16 }}>
                {[{v:"₹4.2k",l:"Today"},{v:"28",l:"Orders"},{v:"4.8★",l:"Rating"}].map((s,i) => (
                  <div key={i} style={{ background:"rgba(249,115,22,0.08)", border:"1px solid rgba(249,115,22,0.15)", borderRadius:10, padding:"10px 8px", textAlign:"center" }}>
                    <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:".95rem", color:C.orange }}>{s.v}</div>
                    <div style={{ fontSize:".6rem", color:"rgba(255,255,255,0.4)", marginTop:2 }}>{s.l}</div>
                  </div>
                ))}
              </div>
              {/* Order cards */}
              <div style={{ fontSize:".6rem", color:"rgba(255,255,255,0.3)", fontWeight:700, letterSpacing:".5px", textTransform:"uppercase", marginBottom:8 }}>Live Orders</div>
              {[
                { icon:"🍛", name:"Butter Chicken + Naan", area:"Civil Lines", status:"Preparing", statusColor:"rgba(22,163,74,0.2)", statusText:"#4ade80" },
                { icon:"📦", name:"Document Delivery",     area:"Sindhi Camp", status:"Picked Up", statusColor:"rgba(37,99,235,0.2)", statusText:"#60a5fa" },
                { icon:"🛒", name:"Grocery Order ×8",      area:"Ramdaspeth",  status:"New Order", statusColor:"rgba(249,115,22,0.2)", statusText:"#fb923c" },
              ].map((o,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:12, padding:"10px 12px", marginBottom:8 }}>
                  <span style={{ fontSize:"1.1rem" }}>{o.icon}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:".72rem", fontWeight:600, color:"rgba(255,255,255,0.85)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{o.name}</div>
                    <div style={{ fontSize:".6rem", color:"rgba(255,255,255,0.35)", marginTop:2 }}>{o.area}</div>
                  </div>
                  <span style={{ background:o.statusColor, color:o.statusText, fontSize:".6rem", fontWeight:700, padding:"2px 8px", borderRadius:100, flexShrink:0 }}>{o.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PARTNER TYPES ── */}
      <section style={{ padding:"80px 5%", background:"rgba(255,255,255,0.015)" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <div style={{ display:"inline-block", background:"rgba(249,115,22,0.1)", color:C.orange, fontSize:".72rem", fontWeight:700, padding:"5px 14px", borderRadius:100, letterSpacing:"1px", textTransform:"uppercase", marginBottom:14, border:"1px solid rgba(249,115,22,0.2)" }}>
              Who can partner
            </div>
            <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"clamp(1.8rem,4vw,2.8rem)", letterSpacing:"-1.5px", marginBottom:12 }}>4 ways to earn with Quickry</h2>
            <p style={{ color:"rgba(255,255,255,0.5)", fontSize:".95rem" }}>Akola ke sabse fast delivery platform par apna business grow karo</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:16 }}>
            {PARTNER_TYPES.map((pt, i) => {
              const Icon = pt.icon;
              return (
                <div key={i}
                  style={{ background:"linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))", border:"1px solid rgba(255,255,255,0.07)", borderRadius:20, padding:28, transition:"all .3s", cursor:"pointer", position:"relative", overflow:"hidden" }}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(249,115,22,0.3)";e.currentTarget.style.transform="translateY(-4px)"}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.07)";e.currentTarget.style.transform="translateY(0)"}}>
                  <div style={{ width:52, height:52, borderRadius:16, background:pt.bg, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:16 }}>
                    <Icon size={24} color={pt.color}/>
                  </div>
                  <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1.1rem", marginBottom:8, letterSpacing:"-0.3px" }}>{pt.label}</div>
                  <div style={{ fontSize:".82rem", color:"rgba(255,255,255,0.5)", lineHeight:1.6, marginBottom:16 }}>{pt.sub}</div>
                  <div style={{ fontSize:".8rem", fontWeight:700, color:C.orange, display:"flex", alignItems:"center", gap:6 }}>
                    <TrendingUp size={14}/> {pt.earn} monthly potential
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ padding:"80px 5%" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <div style={{ display:"inline-block", background:"rgba(249,115,22,0.1)", color:C.orange, fontSize:".72rem", fontWeight:700, padding:"5px 14px", borderRadius:100, letterSpacing:"1px", textTransform:"uppercase", marginBottom:14, border:"1px solid rgba(249,115,22,0.2)" }}>Process</div>
            <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"clamp(1.8rem,4vw,2.8rem)", letterSpacing:"-1.5px", marginBottom:12 }}>Start in 4 simple steps</h2>
            <p style={{ color:"rgba(255,255,255,0.5)", fontSize:".95rem" }}>Sirf 48 ghante mein apna business Quickry pe live karo</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:24 }}>
            {STEPS.map((s,i) => (
              <div key={i} style={{ textAlign:"center", padding:"0 16px" }}>
                <div style={{ width:64, height:64, borderRadius:"50%", background:"linear-gradient(135deg,rgba(249,115,22,0.15),rgba(249,115,22,0.08))", border:"1px solid rgba(249,115,22,0.25)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.3rem", color:C.orange }}>
                  {s.num}
                </div>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:"1rem", marginBottom:8, letterSpacing:"-0.3px" }}>{s.title}</div>
                <div style={{ fontSize:".8rem", color:"rgba(255,255,255,0.5)", lineHeight:1.6 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section id="benefits" style={{ padding:"80px 5%", background:"rgba(255,255,255,0.015)" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"center" }}>
            <div>
              <div style={{ display:"inline-block", background:"rgba(249,115,22,0.1)", color:C.orange, fontSize:".72rem", fontWeight:700, padding:"5px 14px", borderRadius:100, letterSpacing:"1px", textTransform:"uppercase", marginBottom:14, border:"1px solid rgba(249,115,22,0.2)" }}>Why Quickry</div>
              <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"clamp(1.8rem,4vw,2.8rem)", letterSpacing:"-1.5px", marginBottom:14 }}>Everything you<br/>need to succeed</h2>
              <p style={{ color:"rgba(255,255,255,0.5)", fontSize:".95rem", lineHeight:1.7, marginBottom:28 }}>Sirf orders nahi — growth ke liye poora ecosystem dete hain.</p>
              <button onClick={()=>navigate("/register")}
                style={{ background:`linear-gradient(135deg,${C.orangeDark},${C.orange})`, border:"none", borderRadius:12, padding:"13px 26px", color:"#fff", fontWeight:700, fontSize:".9rem", fontFamily:"inherit", boxShadow:"0 6px 20px rgba(249,115,22,0.3)", transition:"all .2s", display:"flex", alignItems:"center", gap:8 }}
                onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
                onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
                Partner bano — Free! <ChevronRight size={16}/>
              </button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {BENEFITS.map((b,i) => (
                <div key={i}
                  style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:16, padding:20, transition:"border-color .2s" }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(249,115,22,0.2)"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.07)"}>
                  <div style={{ width:38, height:38, borderRadius:11, background:`${b.color}18`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 }}>
                    <b.icon size={20} color={b.color} strokeWidth={1.8}/>
                  </div>
                  <div style={{ fontWeight:700, fontSize:".88rem", marginBottom:5 }}>{b.title}</div>
                  <div style={{ fontSize:".75rem", color:"rgba(255,255,255,0.45)", lineHeight:1.6 }}>{b.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" style={{ padding:"80px 5%" }}>
        <div style={{ maxWidth:720, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <div style={{ display:"inline-block", background:"rgba(249,115,22,0.1)", color:C.orange, fontSize:".72rem", fontWeight:700, padding:"5px 14px", borderRadius:100, letterSpacing:"1px", textTransform:"uppercase", marginBottom:14, border:"1px solid rgba(249,115,22,0.2)" }}>FAQ</div>
            <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"clamp(1.8rem,4vw,2.5rem)", letterSpacing:"-1.5px" }}>Common questions</h2>
          </div>
          {FAQS.map((f,i) => (
            <div key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
              <div onClick={()=>setOpenFaq(openFaq===i?null:i)}
                style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 0", cursor:"pointer", gap:16 }}>
                <span style={{ fontWeight:600, fontSize:".92rem", color:"rgba(255,255,255,0.85)" }}>{f.q}</span>
                <div style={{ width:26, height:26, borderRadius:"50%", background:openFaq===i?"rgba(249,115,22,0.15)":"rgba(255,255,255,0.06)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .3s", color:openFaq===i?C.orange:"rgba(255,255,255,0.5)", transform:openFaq===i?"rotate(45deg)":"rotate(0)", fontSize:"1rem" }}>
                  +
                </div>
              </div>
              <div style={{ maxHeight:openFaq===i?200:0, overflow:"hidden", transition:"max-height .35s ease", fontSize:".83rem", color:"rgba(255,255,255,0.5)", lineHeight:1.7, paddingBottom:openFaq===i?18:0 }}>
                {f.a}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <div style={{ margin:"0 5% 80px" }}>
        <div style={{ background:`linear-gradient(135deg,${C.orangeDark} 0%,${C.orange} 50%,#fb923c 100%)`, borderRadius:28, padding:"64px 48px", textAlign:"center", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", inset:0, opacity:0.05, backgroundImage:"url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='%23fff' d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/svg%3E\")" }}/>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"clamp(1.8rem,4vw,2.6rem)", letterSpacing:"-1.5px", marginBottom:14, position:"relative" }}>
            Ready to grow your business? 🚀
          </h2>
          <p style={{ fontSize:"1rem", opacity:.85, marginBottom:32, position:"relative" }}>
            1000+ Akola customers aapke orders ka wait kar rahe hain
          </p>
          <button onClick={()=>navigate("/register")}
            style={{ background:"#fff", color:C.orange, border:"none", borderRadius:14, padding:"15px 36px", fontWeight:800, fontSize:"1rem", fontFamily:"inherit", cursor:"pointer", transition:"all .25s", position:"relative" }}
            onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 10px 32px rgba(0,0,0,0.2)"}}
            onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none"}}>
            Partner bano — It's Free!
          </button>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer style={{ background:"#080808", borderTop:"1px solid rgba(255,255,255,0.06)", padding:"48px 5% 32px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:40, marginBottom:40 }}>
            <div>
              <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.4rem", letterSpacing:"-1px", marginBottom:12 }}>Quick<span style={{ color:C.orange }}>ry</span></div>
              <p style={{ fontSize:".82rem", color:"rgba(255,255,255,0.4)", lineHeight:1.7, maxWidth:280 }}>Akola ka sabse fast delivery platform. Restaurants, grocery stores, courier services ke liye best partner.</p>
            </div>
            {[
              { title:"Company", links:["About Us","Careers","Blog","Press"] },
              { title:"Support", links:["Help Center","Contact Us","Privacy Policy","Terms"] },
            ].map((col,i) => (
              <div key={i}>
                <div style={{ fontWeight:700, fontSize:".78rem", color:"rgba(255,255,255,0.4)", letterSpacing:"1px", textTransform:"uppercase", marginBottom:16 }}>{col.title}</div>
                {col.links.map((l,j) => (
                  <div key={j} style={{ color:"rgba(255,255,255,0.45)", fontSize:".82rem", marginBottom:10, cursor:"pointer", transition:"color .2s" }}
                    onMouseEnter={e=>e.target.style.color="#fff"}
                    onMouseLeave={e=>e.target.style.color="rgba(255,255,255,0.45)"}>
                    {l}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)", paddingTop:24, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
            <p style={{ fontSize:".72rem", color:"rgba(255,255,255,0.3)" }}>© 2025 Quickry. All rights reserved. Made with ❤️ in Akola, Maharashtra.</p>
            <p style={{ fontSize:".72rem", color:"rgba(255,255,255,0.3)" }}>📧 partner@quickry.in</p>
          </div>
        </div>
      </footer>
    </div>
  );
}